// Mounts <VideoPlayer> for the Unpoly app and holds everything that only exists there: the
// `app:main-player:*` control channel, the career dialog, the picture-in-picture side effects, and the
// two call-to-action banners' navigation. Only the Unpoly bundle imports this module, so `up` is fair
// game here — unlike in the component and its siblings.

import { mount, unmount } from 'svelte'
import VideoPlayer from 'frontend/shared/javascripts/video_player/VideoPlayer.svelte'
import type { VideoPlayerData } from 'frontend/shared/javascripts/video_player/types'
import type PlayerClass from 'player/javascripts/player'
import { trackEvent } from 'frontend/shared/javascripts/util/analytics'
import session from 'frontend/shared/javascripts/util/session'
import { isNativeApp } from 'shared/javascripts/util/detection'
import { deviceDetector } from 'shared/javascripts/util/is_mobile'
import { clickAd } from 'player/javascripts/player/advertising'
import { playbackParams } from 'player/javascripts/player/playback_params'
import { environmentAllowsAutoplay } from 'player/javascripts/player/autoplay'

interface VideoPlayerApi {
  play(): void
  pause(): void
  pauseAndExitFullscreen(): void
  preventPlayback(): void
  allowPlayback(): void
  suspendForModal(): void
  resumeAfterModal(): void
  player(): Promise<InstanceType<typeof PlayerClass>>
}

export interface MountVideoPlayerOptions {
  /** Unpoly re-runs compilers when it revalidates a cached fragment, which is not a new page view. */
  skipVisitBookkeeping?: boolean
}

/** How much earlier than the technical start of the footer the mini player fades out. */
const FADE_TOLERANCE = 160

export function mountVideoPlayer(
  element: HTMLElement,
  data: VideoPlayerData,
  { skipVisitBookkeeping }: MountVideoPlayerOptions = {},
): () => void {
  const video = data.video
  const footer = document.querySelector<HTMLElement>('.frontend-layout--footer')
  const careerDialogEnabled = video.career_dialog_enabled

  const { startTime, endTime, forceAutoplay } = playbackParams(up.history.location, video.timestamp)
  const autoplay =
    (data.allow_autoplay && !data.embedded && environmentAllowsAutoplay()) || forceAutoplay

  let pipFaded = false
  let playerContainer!: HTMLElement

  function play(): void {
    component.play()
  }

  function pauseAndExitFullscreen(): void {
    component.pauseAndExitFullscreen()
  }

  function preventPlayback(): void {
    component.preventPlayback()
  }

  function allowPlayback(): void {
    component.allowPlayback()
  }

  function onModalOpen(): void {
    component.suspendForModal()
  }

  function onModalClosed(): void {
    component.resumeAfterModal()
  }

  function onTimestampSeek(event: Event & { timestamp: number }): void {
    void component.player().then((player) => player.seek(event.timestamp))
  }

  // ---------------------------------------------------------------------------------------------
  // Call-to-action banners
  // ---------------------------------------------------------------------------------------------

  function onQuizRequested(): void {
    up.emit('player:switch:quiz')
  }

  function onLearningExercisesRequested(event: MouseEvent, exercisesUrl: string): void {
    if (up.event.isModified(event)) {
      return // let cmd/middle-click open a tab
    }
    event.preventDefault()
    void up.navigate({
      url: exercisesUrl,
      target: '.video-layout-learning--content',
      history: true,
      scroll: '#learning-exercises',
      scrollBehavior: 'smooth',
      revealTop: true,
    })
  }

  // ---------------------------------------------------------------------------------------------
  // Picture-in-picture side effects the component leaves to the page
  // ---------------------------------------------------------------------------------------------

  function fadedNow(): boolean {
    if (!footer) {
      return false
    }
    return window.innerHeight > footer.getBoundingClientRect().top + FADE_TOLERANCE
  }

  function updateFade(): void {
    const faded = fadedNow()
    if (faded === pipFaded) {
      return
    }
    pipFaded = faded
    playerContainer.classList.toggle('-faded', pipFaded)
    // tracking: player.pip_fade
    trackEvent('player.togglePipFaded', { pipFaded })
  }

  function onDockedChange(docked: boolean): void {
    element.closest('[data-pip-container]')?.classList.toggle('-pip', docked)
    up.emit('app.togglePip', { pipVisible: docked })

    if (docked) {
      up.on(window, 'scroll resize', updateFade)
      updateFade()
    } else {
      up.off(window, 'scroll resize', updateFade)
      if (pipFaded) {
        pipFaded = false
        playerContainer.classList.remove('-faded')
        // tracking: player.pip_fade
        trackEvent('player.togglePipFaded', { pipFaded })
      }
    }
  }

  // ---------------------------------------------------------------------------------------------
  // The career dialog
  // ---------------------------------------------------------------------------------------------

  function playerScrolledAway(): boolean {
    if (deviceDetector.isMobile()) {
      return element.closest('.player-toggle-content')?.classList.contains('-sticky') ?? false
    } else {
      return element.getBoundingClientRect().bottom < 0
    }
  }

  function setReminderTimestamp(): void {
    session.lastCareerReminder = String(Date.now())
  }

  function reminderTimePassed(): boolean {
    if (session.lastCareerReminder === null) {
      return true
    }
    const differenceMs = Date.now() - Number(session.lastCareerReminder)
    const differenceSec = Math.floor(differenceMs / 1000)
    return differenceSec >= video.show_career_dialog_after
  }

  function showCareerDialog(): void {
    setReminderTimestamp()

    void up.layer.open({
      url: video.career_dialog_path,
      dismissible: ['key', 'button'],
      history: false,
    })
  }

  function onScroll(): void {
    void component.player().then(() => {
      if (careerDialogEnabled && playerScrolledAway() && reminderTimePassed()) {
        if (up.layer.isRoot() && !isNativeApp()) {
          showCareerDialog()
        }
      }
    })
  }

  // ---------------------------------------------------------------------------------------------

  function clearPosterBackdrop(): void {
    const videoPlayerElement = element.closest<HTMLElement>('.video-player')
    if (videoPlayerElement) {
      videoPlayerElement.style.backgroundImage = 'none'
    }
  }

  const component = mount(VideoPlayer, {
    target: element,
    props: {
      ...data,
      skipVisitBookkeeping,
      startPosition: startTime,
      endPosition: endTime,
      autoplay,
      dockOwner: true,
      onQuizRequested,
      onLearningExercisesRequested,
      onDockedChange,
    },
  }) as unknown as VideoPlayerApi

  playerContainer = element.querySelector<HTMLElement>('[data-player--container]')!

  void component.player().then((newPlayer) => {
    const videojsPlayer = newPlayer.videojsPlayer

    videojsPlayer.one('adsready', () => {
      up.emit<{ videoId: number; triggerClick: () => void }>('player:received-click-through-url', {
        videoId: video.id,
        triggerClick() {
          clickAd(videojsPlayer)
        },
      })
    })
  })

  if (careerDialogEnabled) {
    up.on(window, 'scroll', onScroll)
  }
  up.on('app:main-player:play', play)
  up.on('app:main-player:pause', pauseAndExitFullscreen)
  up.on('app:main-player:move-to-background', preventPlayback)
  up.on('app:main-player:move-to-foreground', allowPlayback)

  up.on(playerContainer, 'app:player:play', { once: true }, clearPosterBackdrop)

  up.on('up:layer:opened', onModalOpen)
  up.on('up:layer:dismissed', onModalClosed)
  up.on('timestamp:seek', onTimestampSeek)

  return () => {
    if (careerDialogEnabled) {
      up.off(window, 'scroll', onScroll)
    }
    up.off(window, 'scroll resize', updateFade)

    up.off('app:main-player:play', play)
    up.off('app:main-player:pause', pauseAndExitFullscreen)
    up.off('app:main-player:move-to-background', preventPlayback)
    up.off('app:main-player:move-to-foreground', allowPlayback)

    up.off('up:layer:opened', onModalOpen)
    up.off('up:layer:dismissed', onModalClosed)
    up.off('timestamp:seek', onTimestampSeek)

    void unmount(component)
  }
}
